// File: src/components/EntryFormModal.js
// Entering an event, as a guest or as a signed-in competitor.
//
// A guest gets a manage link back, shown once - there is no email in this
// system - so the success panel is part of the same modal rather than a
// separate screen the entrant might close too early.

import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Copy, AlertTriangle, UserPlus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';
import { apiGet, apiPost } from '../services/api';
import TurnstileWidget from './TurnstileWidget';
import EntryExtrasFields, { answersToPayload } from './EntryExtrasFields';
import CountryOptions from './CountryOptions';

const EMPTY = {
  first_name: '', last_name: '',
  car_number: '', club: '', birth_year: '', email: '', phone: '', notes: ''
};

// One datalist shared by every class row. The id has to be unique on the page,
// and there is only ever one entry modal open.
const TRANSPONDER_LIST_ID = 'my-transponders';

// Entries made without an account are remembered here, so an entrant who closes
// the tab can still find their manage link on the same device.
const DEVICE_ENTRIES_KEY = 'myEntries';

const rememberOnDevice = (entry) => {
  try {
    const saved = JSON.parse(localStorage.getItem(DEVICE_ENTRIES_KEY) || '[]');
    saved.unshift(entry);
    localStorage.setItem(DEVICE_ENTRIES_KEY, JSON.stringify(saved.slice(0, 20)));
  } catch {
    // A full or disabled localStorage must not break the entry itself.
  }
};

function EntryFormModal({ event, token, onClose, onEntered, onSignedIn }) {
  const { t } = useLanguage();
  const { currentCountry } = useCountry();
  const [form, setForm] = useState(EMPTY);
  const [wantsAccount, setWantsAccount] = useState(false);
  // The country is where the entrant LIVES, not where the race is - the two
  // differ often enough that guessing from the event would be wrong. The one
  // they picked in the header is the better guess, and they can change it.
  const [account, setAccount] = useState({ password: '', country: currentCountry.code });
  // Class id -> the transponder declared for that class. A class is selected
  // exactly when it has a key here, so the two cannot fall out of step.
  const [classTransponders, setClassTransponders] = useState({});
  // What this competitor has used before, offered in the datalist.
  const [savedTransponders, setSavedTransponders] = useState([]);
  // Which number they used last time they ran each of these classes.
  const [suggestions, setSuggestions] = useState({});
  // What the organizer asked about, keyed by question id. Empty for an event
  // that asks nothing, which is most of them.
  const [answers, setAnswers] = useState({});
  const [turnstileToken, setTurnstileToken] = useState('');
  const [resetSignal, setResetSignal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Used to reject submissions faster than a person could read the form.
  const startedAt = useRef(Date.now());

  // A signed-in competitor should not retype what their profile already holds.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    apiGet('/me/profile', { token })
      .then(profile => {
        if (cancelled) return;
        setSavedTransponders(profile.transponders || []);
        setForm(current => {
          const prefilled = { ...current };
          for (const field of Object.keys(EMPTY)) {
            // Never overwrite something already typed in this session.
            if (!prefilled[field] && profile[field]) prefilled[field] = String(profile[field]);
          }
          return prefilled;
        });
      })
      .catch(() => {
        // Prefilling is a convenience; entering must still work without it.
      });

    return () => { cancelled = true; };
  }, [token]);

  // Answered per event, so it cannot ride along with the profile.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    apiGet(`/me/transponder-suggestions?event_id=${event.id}`, { token })
      .then(byClass => { if (!cancelled) setSuggestions(byClass || {}); })
      .catch(() => {
        // A suggestion is a convenience; the field is still typeable.
      });

    return () => { cancelled = true; };
  }, [token, event.id]);

  const openClasses = event.classes || [];
  const questions = event.questions || [];
  const selectedClassIds = Object.keys(classTransponders).map(Number);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const toggleClass = (id) => {
    setClassTransponders(prev => {
      if (id in prev) {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      // Ticking a class fills in the transponder it was run with before, so the
      // common case needs no typing at all.
      return { ...prev, [id]: suggestions[id] || '' };
    });
  };

  const setClassTransponder = (id, value) =>
    setClassTransponders(prev => ({ ...prev, [id]: value }));

  const submit = async () => {
    if (selectedClassIds.length === 0) {
      setError(t('entryPickAtLeastOneClass'));
      return;
    }
    if (wantsAccount && !form.email.trim()) {
      setError(t('entryEmailRequiredForAccount'));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const created = await apiPost('/public/entries', {
        ...form,
        event_id: event.id,
        classes: selectedClassIds.map(id => ({
          event_class_id: id,
          transponder_number: classTransponders[id]
        })),
        answers: answersToPayload(answers, questions),
        form_started_at: startedAt.current,
        website_url: '',                       // honeypot: people leave this empty
        'cf-turnstile-response': turnstileToken,
        // Signing up happens in the same request: a Turnstile token is
        // single-use, so registering separately would need a second challenge.
        account: wantsAccount && !token ? account : undefined
      }, { token });

      // The server signs the new account in, so there is no second password
      // prompt moments after they chose one.
      if (created.account && created.account.token) {
        localStorage.setItem('token', created.account.token);
        localStorage.setItem('userRole', created.account.user.role);
        localStorage.setItem('username', created.account.user.username);
        if (onSignedIn) onSignedIn(created.account.user);
      }

      if (created.manage_token) {
        rememberOnDevice({
          token: created.manage_token,
          event_id: event.id,
          event_name: event.name,
          saved_at: new Date().toISOString()
        });
      }

      setResult(created);
      if (onEntered) onEntered();
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('entryFailed'));
      // The token is single-use: without a reset the retry fails again.
      setTurnstileToken('');
      setResetSignal(n => n + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const manageUrl = result && result.manage_token
    ? `${window.location.origin}${window.location.pathname}#/entry/${result.manage_token}`
    : null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(manageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked; the link is on screen to copy by hand.
    }
  };

  const field = (name, label, { type = 'text', required = false } = {}) => (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">
        {label}{required ? '' : ` (${t('entryOptional')})`}
      </label>
      <input
        type={type}
        value={form[name]}
        onChange={set(name)}
        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-paper rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-line">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-ink">
            {result ? t('entrySuccessTitle') : t('entryTitle')}
          </h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" title={t('close')}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {result ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded flex items-start gap-2">
              <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{event.name}</p>
                {result.status === 'waitlisted' && <p className="mt-1">{t('entrySuccessWaitlisted')}</p>}
              </div>
            </div>

            {result.account && (
              <div className="bg-paper-alt rounded p-4 flex items-start gap-2">
                <UserPlus className="w-5 h-5 text-ink-muted flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-ink font-semibold">{t('entryAccountCreated')}</p>
                  <p className="text-ink-muted text-sm mt-1">
                    {t('entrySignedInAs')} {result.account.user.username}
                  </p>
                </div>
              </div>
            )}

            {result.warnings && result.warnings.includes('duplicateTransponder') && (
              <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {t('entryDuplicateTransponder')}
              </div>
            )}

            {/* Shown once. There is no email in this system, so losing this link
                means asking the organizer to make the change instead. */}
            {manageUrl && (
              <div className="bg-paper-alt rounded p-4">
                <h4 className="text-ink font-semibold mb-1">{t('entryManageLinkTitle')}</h4>
                <p className="text-ink-muted text-sm mb-3">{t('entryManageLinkHelp')}</p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={manageUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 bg-paper border border-line rounded text-ink text-sm font-mono-num"
                  />
                  <button
                    onClick={copyLink}
                    className="bg-accent hover:opacity-90 text-accent-ink px-4 py-2 rounded font-semibold flex items-center gap-2 whitespace-nowrap"
                  >
                    <Copy className="w-4 h-4" />
                    {copied ? t('entryLinkCopied') : t('entryCopyLink')}
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
            >
              {t('close')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-ink-muted mb-2 font-semibold">{t('entrySelectClasses')}</label>
              <p className="text-ink-muted text-sm mb-2">{t('entryTransponderPerClassHint')}</p>

              {/* Offered to every transponder input on the form. Empty for a
                  guest, which leaves a plain text box. */}
              <datalist id={TRANSPONDER_LIST_ID}>
                {savedTransponders.map(saved => (
                  <option key={saved.id} value={saved.number}>
                    {saved.label || ''}
                  </option>
                ))}
              </datalist>

              <div className="space-y-2">
                {openClasses.map(cls => {
                  const full = cls.entry_limit && cls.entry_count >= cls.entry_limit;
                  const selected = cls.id in classTransponders;
                  return (
                    <div key={cls.id} className="bg-paper-alt px-3 py-2 rounded">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleClass(cls.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-ink flex-1">{cls.name}</span>
                        {full && (
                          <span className="text-xs text-red-700 dark:text-red-400 font-semibold">
                            {t('eventEntryLimitReached')}
                          </span>
                        )}
                      </label>

                      {/* One control that does both things: pick a saved
                          number, or type a new one - which is then saved. */}
                      {selected && (
                        <div className="mt-2 pl-6 flex items-center gap-2">
                          <span className="text-ink-muted text-sm whitespace-nowrap">
                            {t('entryClassTransponder')}
                          </span>
                          <input
                            type="text"
                            list={TRANSPONDER_LIST_ID}
                            value={classTransponders[cls.id]}
                            onChange={(e) => setClassTransponder(cls.id, e.target.value)}
                            placeholder={t('transponderNumberPlaceholder')}
                            className="flex-1 min-w-0 px-3 py-1.5 bg-paper border border-line rounded text-ink font-mono-num"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <EntryExtrasFields
              questions={questions}
              answers={answers}
              onChange={setAnswers}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('first_name', t('entryFirstName'), { required: true })}
              {field('last_name', t('entryLastName'), { required: true })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {field('car_number', t('entryCarNumber'), { type: 'number' })}
              {field('club', t('entryClub'))}
              {field('birth_year', t('entryBirthYear'), { type: 'number' })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('email', t('entryEmail'), { type: 'email' })}
              {field('phone', t('entryPhone'))}
            </div>

            {field('notes', t('entryNotes'))}

            {/* Honeypot: off-screen and hidden from assistive tech, so only a
                bot filling every field will touch it. */}
            <input
              type="text"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute left-[-9999px] w-px h-px opacity-0"
              onChange={() => {}}
            />

            {/* Only offered to people who are not already signed in. */}
            {!token && (
              <div className="bg-paper-alt rounded p-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantsAccount}
                    onChange={(e) => setWantsAccount(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <UserPlus className="w-4 h-4 text-ink-muted" />
                  <span className="text-ink font-semibold">{t('entryCreateAccount')}</span>
                </label>
                <p className="text-ink-muted text-sm mt-2">{t('entryCreateAccountHelp')}</p>

                {wantsAccount && (
                  <div className="mt-4 space-y-4">
                    {/* There is no username to invent: the email above is the
                        login, so it is shown here rather than asked for twice. */}
                    <div>
                      <label className="block text-ink-muted mb-2 font-semibold">{t('entryLoginIsEmail')}</label>
                      <input
                        type="email"
                        autoComplete="username"
                        value={form.email}
                        onChange={set('email')}
                        placeholder={t('entryEmail')}
                        className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                      />
                      <p className="text-ink-muted text-xs mt-1">{t('entryLoginIsEmailHint')}</p>
                    </div>
                    <div>
                      <label className="block text-ink-muted mb-2 font-semibold">{t('entryPassword')}</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={account.password}
                        onChange={(e) => setAccount({ ...account, password: e.target.value })}
                        className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                      />
                      <p className="text-ink-muted text-xs mt-1">{t('entryPasswordHint')}</p>
                    </div>
                    <div>
                      <label className="block text-ink-muted mb-2 font-semibold"
                             htmlFor="entry-account-country">
                        {t('country')}
                      </label>
                      <select
                        id="entry-account-country"
                        value={account.country}
                        onChange={(e) => setAccount({ ...account, country: e.target.value })}
                        className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                      >
                        <CountryOptions />
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            <TurnstileWidget onToken={setTurnstileToken} resetSignal={resetSignal} />

            {error && (
              <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={submit}
                disabled={submitting || !turnstileToken}
                className="flex-1 bg-accent hover:opacity-90 disabled:bg-paper-alt disabled:text-ink-muted text-accent-ink py-3 rounded font-semibold"
              >
                {submitting ? t('entrySubmitting') : t('entrySubmit')}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-3 rounded font-semibold"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EntryFormModal;
